import { useCallback, useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { ApiError, request, requestStream, newIdempotencyKey } from "./client";
import { useSettings } from "./settings";
import { identify, resetAnalytics, track } from "@/shared/analytics";
import {
  AuthConfigResponse,
  CreateKeyResponse,
  CreateOrderResponse,
  CryptoSubmitResponse,
  DetectResponse,
  HumanizeResponse,
  GoogleAuthResponse,
  HumanizeStreamEvent,
  KeyList,
  LoginResponse,
  MeResponse,
  NoContent,
  OrderStatusResponse,
  OrderList,
  ProgressResponse,
  type RoundDraft,
  SendCodeResponse,
  SignupResponse,
} from "./schemas";

const TERMINAL = new Set(["paid", "failed", "closed"]);
export const isTerminal = (status: string) => TERMINAL.has(status);

/**
 * A stable Idempotency-Key keyed by an operation "signature". The same signature
 * returns the SAME key (so retrying the same intent is collapsed server-side);
 * a changed signature rolls a fresh key (a genuinely new intent). `reset()` (on
 * success) forces the next identical submit to be a new, separately-charged op.
 */
function useStableIdemKey() {
  const ref = useRef<{ sig: string; key: string } | null>(null);
  const keyFor = useCallback((sig: string) => {
    if (!ref.current || ref.current.sig !== sig) {
      ref.current = { sig, key: newIdempotencyKey() };
    }
    return ref.current.key;
  }, []);
  const reset = useCallback(() => {
    ref.current = null;
  }, []);
  return { keyFor, reset };
}

/** Account balance + recent usage. Only runs once an API key is set. */
export function useMe(): UseQueryResult<MeResponse> {
  const { settings, hasKey } = useSettings();
  return useQuery({
    queryKey: ["me", settings.apiBase, settings.jwt, settings.apiKey],
    enabled: hasKey,
    queryFn: () => request(settings, "/me", MeResponse),
    staleTime: 5_000,
  });
}

export interface HumanizeInput {
  text: string;
  strength: number;
  verify?: boolean;
  /** Client id for the live progress channel (poll via useHumanizeProgress). */
  progressId?: string;
}

/** Humanize a passage; refreshes the balance on success. */
export function useHumanize() {
  const { settings } = useSettings();
  const qc = useQueryClient();
  const { keyFor, reset } = useStableIdemKey();
  return useMutation({
    mutationFn: (input: HumanizeInput) => {
      const verify = input.verify ?? false;
      return request(settings, "/humanize", HumanizeResponse, {
        method: "POST",
        idempotencyKey: keyFor(JSON.stringify([input.text, input.strength, verify])),
        headers: input.progressId ? { "X-Progress-Id": input.progressId } : undefined,
        body: { text: input.text, strength: input.strength, verify },
      });
    },
    onSuccess: () => {
      reset();
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/** Live state of one streaming humanize run. */
export interface HumanizeStreamState {
  isPending: boolean;
  /** People ahead in the GPU queue; null once the job is running. */
  queuePosition: number | null;
  /** Rough seconds until this job starts; null once running. */
  queueEta: number | null;
  /** Optimistic draft of the CURRENT round, growing token by token. */
  liveText: string;
  round: number;
  total: number;
  /** Parts in chunked-parallel mode; null for short single-block inputs. */
  segTotal: number | null;
  /** Per-part live buffers (chunked mode streams all parts concurrently). */
  liveSegs: string[];
  /** Completed round snapshots keyed by round number (worker sends 1/3/5). */
  drafts: Map<number, RoundDraft>;
  /** Terminal committed response; null until the `done` event lands. */
  result: HumanizeResponse | null;
  error: string | null;
}

const STREAM_IDLE: HumanizeStreamState = {
  isPending: false,
  queuePosition: null,
  queueEta: null,
  liveText: "",
  round: 0,
  total: 0,
  segTotal: null,
  liveSegs: [],
  drafts: new Map(),
  result: null,
  error: null,
};

function reduceStream(
  s: HumanizeStreamState,
  ev: HumanizeStreamEvent,
): HumanizeStreamState {
  switch (ev.type) {
    case "queued":
      return { ...s, queuePosition: ev.position, queueEta: ev.eta_secs };
    case "segments":
      // Chunked-parallel plan: render one live pane per part.
      return {
        ...s,
        segTotal: ev.total,
        liveSegs: Array(ev.total).fill(""),
        queuePosition: null,
        queueEta: null,
      };
    case "round":
      // A new round rewrites the previous draft — clear the live pane(s).
      // The job is running now, so any queue banner disappears.
      return {
        ...s,
        round: ev.round,
        total: ev.total,
        liveText: "",
        liveSegs: s.segTotal != null ? Array(s.segTotal).fill("") : [],
        queuePosition: null,
        queueEta: null,
      };
    case "reset": {
      // The draft failed the fidelity gate mid-round; a resample restreams.
      if (ev.segment != null && s.liveSegs.length > 0) {
        const liveSegs = [...s.liveSegs];
        liveSegs[ev.segment - 1] = "";
        return { ...s, liveSegs };
      }
      return { ...s, liveText: "" };
    }
    case "delta": {
      if (ev.segment != null && s.liveSegs.length > 0) {
        const liveSegs = [...s.liveSegs];
        liveSegs[ev.segment - 1] += ev.text;
        return { ...s, liveSegs };
      }
      return { ...s, liveText: s.liveText + ev.text };
    }
    case "round_done": {
      const drafts = new Map(s.drafts);
      drafts.set(ev.round, {
        round: ev.round,
        text: ev.text,
        cos: ev.cos ?? null,
        recommended: false,
        fidelity_ok: ev.ok ?? null,
      });
      // Replace the raw stream with the cleaned (assembled) round text.
      return { ...s, drafts, liveText: ev.text, liveSegs: [] };
    }
    case "done": {
      const { type: _t, ...result } = ev;
      const drafts = new Map(s.drafts);
      for (const d of result.drafts) {
        const previous = drafts.get(d.round);
        // Older orchestrators omit draft-level fidelity flags from `done`.
        // Preserve a streamed verdict only for the same text; otherwise an
        // unverified fallback could become copyable after the final event.
        const fidelityOk = d.fidelity_ok ??
          (previous?.text === d.text ? previous.fidelity_ok : undefined) ?? false;
        drafts.set(d.round, { ...d, fidelity_ok: fidelityOk, recommended: false });
      }
      if (result.round_used != null) drafts.set(result.round_used, {
        round: result.round_used, text: result.output, cos: result.cos,
        fidelity_ok: result.fidelity_ok, recommended: result.fidelity_ok === true,
      });
      return { ...s, drafts, result, liveText: result.output, liveSegs: [], isPending: false };
    }
    case "error":
      return { ...s, error: ev.message, isPending: false };
  }
}

/**
 * Streaming humanize over `POST /humanize/stream` (SSE): tokens render as the
 * GPU emits them (optimistic — a later round/resample may replace the pane),
 * and the terminal `done` event is the same committed HumanizeResponse as the
 * non-stream endpoint. Refreshes the balance once billed.
 */
export function useHumanizeStream() {
  const { settings } = useSettings();
  const qc = useQueryClient();
  const { keyFor, reset } = useStableIdemKey();
  const [state, setState] = useState<HumanizeStreamState>(STREAM_IDLE);
  const runId = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setState({ ...STREAM_IDLE, drafts: new Map() });
    reset();
    return () => {
      ++runId.current;
      abortRef.current?.abort();
    };
  }, [settings.apiBase, settings.jwt, settings.apiKey, reset]);

  const start = useCallback(
    async (input: HumanizeInput) => {
      const verify = input.verify ?? false;
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      const id = ++runId.current;
      // Fresh Map: spreading STREAM_IDLE would reuse the idle drafts instance.
      setState({ ...STREAM_IDLE, isPending: true, drafts: new Map() });
      track("humanize_started", {
        words: input.text.trim().split(/\s+/).length,
        strength: input.strength,
        verify,
      });
      try {
        await requestStream(
          settings,
          "/humanize/stream",
          {
            method: "POST",
            idempotencyKey: keyFor(JSON.stringify([input.text, input.strength, verify])),
            headers: input.progressId ? { "X-Progress-Id": input.progressId } : undefined,
            body: { text: input.text, strength: input.strength, verify },
            signal: ac.signal,
          },
          (raw) => {
            if (id !== runId.current) return;
            const parsed = HumanizeStreamEvent.safeParse(raw);
            if (!parsed.success) return;
            const ev = parsed.data;
            setState((s) => reduceStream(s, ev));
            if (ev.type === "done") {
              reset();
              qc.invalidateQueries({ queryKey: ["me"] });
              track("humanize_done", { rounds: ev.round_used, fidelity_ok: ev.fidelity_ok });
            } else if (ev.type === "error") {
              track("humanize_failed", { message: ev.message });
            }
          },
        );
        if (id !== runId.current) return;
        // Stream closed: if neither `done` nor `error` arrived, the
        // connection dropped mid-run.
        setState((s) =>
          s.result || s.error
            ? { ...s, isPending: false }
            : { ...s, isPending: false, error: "Connection interrupted, please retry" },
        );
      } catch (e) {
        if (id !== runId.current || ac.signal.aborted) return;
        setState((s) => ({
          ...s,
          isPending: false,
          error: e instanceof ApiError ? e.message : "Humanize failed, please retry",
        }));
      }
    },
    [settings, keyFor, reset, qc],
  );

  return { ...state, start };
}

/**
 * Poll the read-only progress channel while a humanize runs. Enabled only while
 * a job is in flight; stops once the worker reports `done`. Best-effort — a
 * missing/`unknown` entry just means "no live data yet".
 */
export function useHumanizeProgress(
  progressId: string | null,
  active: boolean,
): UseQueryResult<ProgressResponse> {
  const { settings } = useSettings();
  return useQuery({
    queryKey: ["humanize-progress", settings.apiBase, progressId],
    enabled: active && !!progressId,
    queryFn: () =>
      request(settings, `/humanize/progress/${progressId}`, ProgressResponse),
    refetchInterval: (query) =>
      query.state.data?.phase === "done" ? false : 800,
    gcTime: 0,
  });
}

/**
 * Score text with the configured AI detector (`POST /detect`). Charged per
 * check unless the server answers from its cache; refreshes the balance
 * when it was.
 */
export function useDetect() {
  const { settings } = useSettings();
  const qc = useQueryClient();
  const { keyFor, reset } = useStableIdemKey();
  const mutation = useMutation({
    mutationFn: (text: string) =>
      request(settings, "/detect", DetectResponse, {
        method: "POST",
        idempotencyKey: keyFor(text),
        body: { text },
      }),
    onSuccess: (res) => {
      reset();
      if (!res.cached) qc.invalidateQueries({ queryKey: ["me"] });
      track("detect_done", {
        provider: res.provider,
        human_prob: res.human_prob,
        cached: res.cached,
        tokens: res.tokens_charged,
      });
    },
  });
  useEffect(() => { mutation.reset(); reset(); }, [settings.apiBase, settings.jwt, settings.apiKey, mutation.reset, reset]);
  return mutation;
}

export interface CreateOrderInput {
  pkg: string;
  /** Rail: "alipay" → pay_url; "usdc"/"usdt"/"crypto" → on-chain fields. */
  provider: string;
}

/** Create an order on the chosen rail. Idempotent on Idempotency-Key. */
export function useCreateOrder() {
  const { settings } = useSettings();
  const { keyFor, reset } = useStableIdemKey();
  return useMutation({
    mutationFn: ({ pkg, provider }: CreateOrderInput) =>
      request(settings, "/billing/orders", CreateOrderResponse, {
        method: "POST",
        idempotencyKey: keyFor(JSON.stringify([pkg, provider])),
        body: { package: pkg, provider },
      }),
    // A successful order is "consumed": the next buy click is a new order.
    onSuccess: (_res, vars) => {
      reset();
      track("order_created", { package: vars.pkg, provider: vars.provider });
    },
  });
}

export interface CryptoSubmitInput {
  out_trade_no: string;
  tx_hash: string;
}

/** Bind a paid tx to a crypto order; on `paid`, refresh the balance. */
export function useCryptoSubmit() {
  const { settings } = useSettings();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CryptoSubmitInput) =>
      request(settings, "/billing/crypto/submit", CryptoSubmitResponse, {
        method: "POST",
        body: { out_trade_no: input.out_trade_no, tx_hash: input.tx_hash.trim() },
      }),
    onSuccess: (res) => {
      if (res.status === "paid") qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ---- Auth (Web session) -------------------------------------------------

export interface Credentials {
  email: string;
  password: string;
  /** Email verification code — required when the server has it enabled. */
  code?: string;
}

/** Whether signup requires an emailed verification code (server flag). */
export function useAuthConfig() {
  const { settings } = useSettings();
  return useQuery({
    queryKey: ["auth-config"],
    queryFn: () => request(settings, "/auth/config", AuthConfigResponse),
    staleTime: 5 * 60 * 1000,
  });
}

/** Request a signup verification code for an email address. */
export function useSendCode() {
  const { settings } = useSettings();
  return useMutation({
    mutationFn: (email: string) =>
      request(settings, "/auth/send-code", SendCodeResponse, {
        method: "POST",
        body: { email: email.trim() },
      }),
  });
}

/** Sign up → store the session JWT + email; returns the one-time api_key too. */
export function useSignup() {
  const { settings, setSettings } = useSettings();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Credentials) =>
      request(settings, "/auth/signup", SignupResponse, {
        method: "POST",
        body: { email: input.email.trim(), password: input.password, code: input.code },
      }),
    onSuccess: (res, vars) => {
      setSettings({ ...settings, jwt: res.jwt, email: vars.email.trim() });
      qc.invalidateQueries();
      identify(vars.email.trim(), { account_id: res.account_id });
      track("signup_done", { method: "email" });
    },
  });
}

/** Sign in / sign up with a Google ID token → store the session JWT + email. */
export function useGoogleAuth() {
  const { settings, setSettings } = useSettings();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (credential: string) =>
      request(settings, "/auth/google", GoogleAuthResponse, {
        method: "POST",
        body: { credential },
      }),
    onSuccess: (res) => {
      setSettings({ ...settings, jwt: res.jwt, email: res.email });
      qc.invalidateQueries();
      identify(res.email, { account_id: res.account_id });
      // api_key is returned only when the account was just created.
      track(res.api_key ? "signup_done" : "login_done", { method: "google" });
    },
  });
}

/** Log in → store the session JWT + email. */
export function useLogin() {
  const { settings, setSettings } = useSettings();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Credentials) =>
      request(settings, "/auth/login", LoginResponse, {
        method: "POST",
        body: { email: input.email.trim(), password: input.password },
      }),
    onSuccess: (res, vars) => {
      setSettings({ ...settings, jwt: res.jwt, email: vars.email.trim() });
      qc.invalidateQueries();
      identify(vars.email.trim());
      track("login_done", { method: "email" });
    },
  });
}

/** Clear the session (and any cached account data). */
export function useLogout() {
  const { settings, setSettings } = useSettings();
  const qc = useQueryClient();
  return () => {
    setSettings({ ...settings, jwt: "", email: "", apiKey: "" });
    qc.clear();
    resetAnalytics();
  };
}

// ---- Self-service API keys ---------------------------------------------

export function useKeys() {
  const { settings, hasKey } = useSettings();
  return useQuery({
    queryKey: ["keys", settings.apiBase, settings.jwt, settings.apiKey],
    enabled: hasKey,
    queryFn: () => request(settings, "/account/keys", KeyList),
    staleTime: 5_000,
  });
}

export function useCreateKey() {
  const { settings } = useSettings();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label?: string) =>
      request(settings, "/account/keys", CreateKeyResponse, {
        method: "POST",
        body: { label: label?.trim() || undefined },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys"] }),
  });
}

export function useRevokeKey() {
  const { settings } = useSettings();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request(settings, `/account/keys/${id}`, NoContent, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keys"] }),
  });
}

/**
 * Poll one order until it reaches a terminal state. Backs off to a steady
 * interval; stops polling once paid/failed/closed. Refreshes balance on pay.
 */
/** Orders already reported as paid, so a re-poll or reload does not re-count. */
const paidReported = new Set<string>();

export function useOrderStatus(outTradeNo: string | null) {
  const { settings } = useSettings();
  const qc = useQueryClient();
  return useQuery({
    // Scope by account + base so switching API Key never reuses another
    // account's cached order.
    queryKey: ["order", settings.apiBase, settings.jwt, settings.apiKey, outTradeNo],
    enabled: !!outTradeNo,
    queryFn: async () => {
      const res = await request(
        settings,
        `/billing/orders/${outTradeNo}`,
        OrderStatusResponse,
      );
      if (res.status === "paid") {
        qc.invalidateQueries({ queryKey: ["me"] });
        if (!paidReported.has(res.out_trade_no)) {
          paidReported.add(res.out_trade_no);
          track("order_paid", {
            package: res.package,
            amount_cents: res.amount_cents,
            currency: res.currency,
            tokens: res.tokens,
          });
        }
      }
      return res;
    },
    retry: 1,
    refetchInterval: (query) => {
      // Stop polling on a hard error (e.g. 404/401) so a stale/foreign order id
      // doesn't generate a 2.5s noise stream; also stop once terminal.
      if (query.state.status === "error") return false;
      const s = query.state.data?.status;
      return s && isTerminal(s) ? false : 2500;
    },
  });
}


export function useOrders() {
  const { settings, hasKey } = useSettings();
  return useQuery({
    queryKey: ["orders", settings.apiBase, settings.jwt, settings.apiKey],
    enabled: hasKey,
    queryFn: () => request(settings, "/billing/orders", OrderList),
    staleTime: 0,
    refetchInterval: (q) => q.state.data?.some(o => !isTerminal(o.status)) ? 5000 : false,
  });
}


export function useSendResetCode() {
  const { settings } = useSettings();
  return useMutation({ mutationFn: (email: string) => request(settings, "/auth/send-reset-code", SendCodeResponse, {
    method: "POST", body: { email: email.trim() },
  }) });
}

export function useResetPassword() {
  const { settings } = useSettings();
  return useMutation({ mutationFn: (input: Credentials) => request(settings, "/auth/reset-password", NoContent, {
    method: "POST", body: { ...input, email: input.email.trim() },
  }) });
}
